/**
 * F33 — Admin trainer certification detail + moderation page (F09 v2 merge).
 *
 * Route: /trainer-verifications/:userId
 * Permission: trainer_verifications:review (super_admin + content_moderator)
 *
 * Shows the trainer's profile summary, sports/specialties, locations-served,
 * and the certification list with presigned doc preview links + per-cert
 * verify/reject/revoke actions. Suspend/Reinstate/Force-deactivate manage the
 * marketplace-visibility moderation state (D21).
 *
 * F09 v2 retired the F33 profile-level approve/reject: the trainer tick is an
 * F09 request decided under `/verifications`. The header shows the trainer's
 * tick (inert) + owner-state badge, and a link to the F09 entity page for
 * admins with `verifications:read`. A trainer without the tick stays visible
 * and bookable — the tick is never a marketplace gate.
 *
 * The cert card + the reason/confirm dialogs are the shared
 * `components/trainer/{TrainerCertCard,TrainerReasonModal,TrainerConfirmDialog}`
 * components (F43-FC §6.4 extraction) so this page and the platform-user Trainer
 * panel render one source.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Spinner,
  StatusChip,
  useToast,
  VerificationBadge,
} from '@bravemove/ui-web';
import {
  adminTrainerVerificationsApi,
  type TrainerVerificationForAdmin,
} from '../../api/trainerVerifications';
import { translateApiError } from '../../auth/errors';
import { TrainerCertCard } from '../../components/trainer/TrainerCertCard';
import { TrainerReasonModal } from '../../components/trainer/TrainerReasonModal';
import { TrainerConfirmDialog } from '../../components/trainer/TrainerConfirmDialog';
import { InertTick } from '../../components/verification/EntityVerificationCard';
import { VERIFICATION_LINK_CLASS, entityPath } from '../../components/verification/labels';
import { useVerificationPermissions } from '../../hooks/useVerificationPermissions';
import { useAuth } from '../../auth/useAuth';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

// ─── Main page ────────────────────────────────────────────────────────────────

type ReasonDialogKind =
  | { kind: 'reject-cert'; certId: string }
  | { kind: 'revoke-cert'; certId: string }
  | { kind: 'suspend' }
  | { kind: 'force-deactivate' };

export function TrainerVerificationDetailPage() {
  const { t, locale } = useI18n();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { canRead: canReadVerifications } = useVerificationPermissions();
  // FIX 5.22 (§13.11-7) — the trainer billing page (route gated `payments:read`)
  // was reachable only from a trainer statement row.
  const canReadBilling = useAuth().adminUser?.permissions.includes('payments:read') ?? false;

  const [detail, setDetail] = useState<TrainerVerificationForAdmin | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linksExpired, setLinksExpired] = useState(false);
  const expiredRef = useRef(false);

  // Reinstate (no reason) confirm dialog.
  const [reinstateOpen, setReinstateOpen] = useState(false);
  const [reinstateLoading, setReinstateLoading] = useState(false);
  const [reinstateError, setReinstateError] = useState<string | null>(null);

  // The single reason-bearing modal, discriminated by `reasonDialog`.
  const [reasonDialog, setReasonDialog] = useState<ReasonDialogKind | null>(null);

  // Per-cert "verify" is a one-tap action (no reason) → track which cert is busy.
  const [busyCertId, setBusyCertId] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    setLinksExpired(false);
    expiredRef.current = false;
    try {
      const data = await adminTrainerVerificationsApi.detail(userId);
      if (data === null) setNotFound(true);
      else setDetail(data);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDetail();
  }, [fetchDetail]);

  const handleLinkExpired = useCallback(() => {
    if (!expiredRef.current) {
      expiredRef.current = true;
      setLinksExpired(true);
    }
  }, []);

  // ─── Moderation actions ─────────────────────────────────────────────────

  const handleReinstate = async () => {
    if (!userId) return;
    setReinstateLoading(true);
    setReinstateError(null);
    try {
      const updated = await adminTrainerVerificationsApi.reinstateTrainer(userId);
      setDetail(updated);
      setReinstateOpen(false);
      toast.push({ tone: 'success', title: t('admin:trainer-verification.toast.reinstated') });
    } catch (err) {
      setReinstateError(translateApiError(err, t));
    } finally {
      setReinstateLoading(false);
    }
  };

  // ─── Reason-bearing actions (single modal) ──────────────────────────────

  const handleReasonSubmit = async (reason: string): Promise<void> => {
    if (!userId || !reasonDialog) return;
    let updated: TrainerVerificationForAdmin | undefined;
    let toastKey = '';
    switch (reasonDialog.kind) {
      case 'reject-cert': {
        await adminTrainerVerificationsApi.rejectCert(reasonDialog.certId, {
          rejectionReason: reason,
        });
        await fetchDetail();
        toast.push({ tone: 'success', title: t('admin:trainer-verification.toast.cert_rejected') });
        setReasonDialog(null);
        return;
      }
      case 'revoke-cert': {
        await adminTrainerVerificationsApi.revokeCert(reasonDialog.certId, {
          rejectionReason: reason,
        });
        await fetchDetail();
        toast.push({ tone: 'success', title: t('admin:trainer-verification.toast.cert_revoked') });
        setReasonDialog(null);
        return;
      }
      case 'suspend':
        updated = await adminTrainerVerificationsApi.suspendTrainer(userId, { reason });
        toastKey = 'admin:trainer-verification.toast.suspended';
        break;
      case 'force-deactivate':
        updated = await adminTrainerVerificationsApi.forceDeactivateTrainer(userId, { reason });
        toastKey = 'admin:trainer-verification.toast.force_deactivated';
        break;
    }
    if (updated) setDetail(updated);
    toast.push({ tone: 'success', title: t(toastKey) });
    setReasonDialog(null);
  };

  const handleVerifyCert = async (certId: string) => {
    setBusyCertId(certId);
    try {
      await adminTrainerVerificationsApi.verifyCert(certId);
      await fetchDetail();
      toast.push({ tone: 'success', title: t('admin:trainer-verification.toast.cert_verified') });
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setBusyCertId(null);
    }
  };

  // ─── Render states ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size={32} />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="space-y-4">
        <Banner tone="warning">{t('admin:trainer-verification.detail.not_found')}</Banner>
        <Button variant="ghost" onClick={() => navigate('/trainer-verifications')}>
          {t('admin:common.back')}
        </Button>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <Banner tone="danger">{error ?? t('admin:errors.generic')}</Banner>
        <Button variant="ghost" onClick={() => navigate('/trainer-verifications')}>
          {t('admin:common.back')}
        </Button>
      </div>
    );
  }

  const isSuspended = detail.suspendedAt !== null;
  const isDeactivated = detail.deactivatedAt !== null;
  // FIX 4.13 — reinstate lifts a suspension or an admin force-deactivate lock.
  const canReinstate = isSuspended || detail.adminDeactivatedAt != null;

  const locationsServed: string[] = [
    detail.servesAffiliatedVenue ? t('admin:trainer-verification.locations.affiliated_venue') : '',
    detail.servesClientVenue ? t('admin:trainer-verification.locations.client_venue') : '',
    detail.servesTrainerOwnLocation
      ? t('admin:trainer-verification.locations.trainer_own_location')
      : '',
  ].filter(Boolean);

  return (
    <div className="space-y-6 max-w-3xl" data-testid="trainer-verification-detail-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 font-bold text-ink">
            {t('admin:trainer-verification.detail.title')}
          </h1>
          <p className="font-mono text-[12px] text-ink-3 mt-0.5">{detail.userId}</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          <InertTick
            verified={detail.verified}
            entityType="trainer"
            size="md"
            testId="trainer-detail-tick"
          />
          <span data-testid="trainer-detail-verification-state">
            <VerificationBadge status={detail.verificationState} size="sm" />
          </span>
          {isSuspended ? (
            <StatusChip
              status="rejected"
              label={t('admin:trainer-verification.detail.suspended_badge')}
              data-testid="trainer-suspended-badge"
            />
          ) : null}
          {isDeactivated ? (
            <StatusChip
              status="pending"
              label={t('admin:trainer-verification.detail.deactivated_badge')}
              data-testid="trainer-deactivated-badge"
            />
          ) : null}
        </div>
      </div>

      {/* F09 v2 — the trainer tick lives in the verifications module. */}
      <Card data-testid="trainer-detail-verification">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[12.5px] text-ink-2">
            {t('admin:trainer-verification.detail.tick_note')}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {canReadVerifications ? (
              <Link
                to={entityPath('trainer', detail.userId)}
                className={VERIFICATION_LINK_CLASS}
                data-testid="trainer-detail-verification-link"
              >
                {t('admin:verification.integrations.openEntity')}
              </Link>
            ) : null}
            {canReadBilling ? (
              <Link
                to={`/trainers/${encodeURIComponent(detail.userId)}/billing`}
                className={VERIFICATION_LINK_CLASS}
                data-testid="trainer-detail-billing-link"
              >
                {t('trainer-billing:title')}
              </Link>
            ) : null}
          </div>
        </div>
      </Card>

      {/* Profile summary */}
      <Card>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[15px] text-ink">{detail.displayName}</span>
          </div>
          {detail.email ? (
            <span className="font-mono text-[12px] text-ink-3">{detail.email}</span>
          ) : null}
          {detail.headline ? <p className="text-[13px] text-ink-2">{detail.headline}</p> : null}
          {detail.bio ? (
            <p className="text-[12.5px] text-ink-2 leading-snug">{detail.bio}</p>
          ) : null}
          <div className="flex flex-wrap gap-1 mt-1">
            {detail.roles.map((role) => (
              <Badge key={role} tone="info" size="sm">
                {role}
              </Badge>
            ))}
          </div>
        </div>
      </Card>

      {/* Sports / specialties + locations + meta */}
      <Card>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[12.5px]">
          <div className="col-span-2">
            <dt className="text-ink-3">{t('admin:trainer-verification.detail.specialties')}</dt>
            <dd className="mt-1 flex flex-wrap gap-1">
              {detail.specialties.length === 0 ? (
                <span className="text-ink-3">—</span>
              ) : (
                detail.specialties.map((s) => (
                  <Badge key={s.sportName} tone={s.isPrimary ? 'accent' : 'neutral'} size="sm">
                    {s.sportName.replace(/-/g, ' ')}
                    {s.specialtyLevel !== null ? ` · ${s.specialtyLevel.toFixed(1)}` : ''}
                  </Badge>
                ))
              )}
            </dd>
          </div>
          <div>
            <dt className="text-ink-3">
              {t('admin:trainer-verification.detail.years_experience')}
            </dt>
            <dd className="text-ink font-medium">{detail.yearsExperience ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-ink-3">{t('admin:trainer-verification.detail.languages')}</dt>
            <dd className="text-ink font-medium uppercase">
              {detail.languages.length > 0 ? detail.languages.join(', ') : '—'}
            </dd>
          </div>
          <div className="col-span-2">
            <dt className="text-ink-3">
              {t('admin:trainer-verification.detail.locations_served')}
            </dt>
            <dd className="text-ink font-medium">
              {locationsServed.length > 0 ? locationsServed.join(' · ') : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-ink-3">
              {t('admin:trainer-verification.detail.oldest_pending_cert')}
            </dt>
            <dd className="text-ink">{formatDate(detail.oldestPendingCertAt, locale)}</dd>
          </div>
          <div>
            <dt className="text-ink-3">{t('admin:trainer-verification.detail.pending_certs')}</dt>
            <dd className="text-ink font-mono tabular-nums">{detail.pendingCertCount}</dd>
          </div>
          {detail.suspensionReason ? (
            <div className="col-span-2">
              <dt className="text-ink-3">
                {t('admin:trainer-verification.detail.suspension_reason')}
              </dt>
              <dd className="text-ink mt-0.5 bg-surface-2 rounded p-2 text-[12px]">
                {detail.suspensionReason}
              </dd>
            </div>
          ) : null}
        </dl>
      </Card>

      {/* Certifications */}
      <div className="space-y-3">
        <h2 className="text-[13px] font-semibold text-ink">
          {t('admin:trainer-verification.detail.certifications')}
        </h2>
        {detail.certifications.length === 0 ? (
          <p className="text-[12.5px] text-ink-3">
            {t('admin:trainer-verification.detail.no_certifications')}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="trainer-certs">
            {detail.certifications.map((cert) => (
              <TrainerCertCard
                key={cert.id}
                cert={cert}
                busy={busyCertId === cert.id}
                canAct
                onExpired={handleLinkExpired}
                onVerify={() => void handleVerifyCert(cert.id)}
                onReject={() => setReasonDialog({ kind: 'reject-cert', certId: cert.id })}
                onRevoke={() => setReasonDialog({ kind: 'revoke-cert', certId: cert.id })}
              />
            ))}
          </div>
        )}
        {linksExpired ? (
          <Button variant="secondary" size="sm" onClick={() => void fetchDetail()}>
            {t('admin:trainer-verification.detail.refresh_links')}
          </Button>
        ) : null}
      </div>

      {/* Moderation bar — suspend / reinstate / force-deactivate */}
      <Card>
        <h2 className="text-[13px] font-semibold text-ink mb-3">
          {t('admin:trainer-verification.detail.moderation')}
        </h2>
        <div className="flex flex-wrap gap-2" data-testid="trainer-moderation-bar">
          {canReinstate ? (
            <Button
              variant="primary"
              onClick={() => {
                setReinstateError(null);
                setReinstateOpen(true);
              }}
              data-testid="trainer-reinstate"
            >
              {t('admin:trainer-verification.detail.reinstate')}
            </Button>
          ) : (
            <Button
              variant="destructive"
              onClick={() => setReasonDialog({ kind: 'suspend' })}
              data-testid="trainer-suspend"
            >
              {t('admin:trainer-verification.detail.suspend')}
            </Button>
          )}
          {!isDeactivated ? (
            <Button
              variant="secondary"
              onClick={() => setReasonDialog({ kind: 'force-deactivate' })}
              data-testid="trainer-force-deactivate"
            >
              {t('admin:trainer-verification.detail.force_deactivate')}
            </Button>
          ) : null}
        </div>
      </Card>

      <button
        type="button"
        className="text-[12px] text-accent hover:underline"
        onClick={() => navigate('/trainer-verifications')}
      >
        {t('admin:trainer-verification.queue.title')}
      </button>

      {/* ── Dialogs ── */}

      <TrainerConfirmDialog
        open={reinstateOpen}
        title={t('admin:trainer-verification.detail.reinstate_confirm_title')}
        body={t('admin:trainer-verification.detail.reinstate_confirm_body')}
        confirmLabel={t('admin:trainer-verification.detail.reinstate')}
        loading={reinstateLoading}
        error={reinstateError}
        onClose={() => setReinstateOpen(false)}
        onConfirm={() => void handleReinstate()}
      />

      <TrainerReasonModal
        open={reasonDialog?.kind === 'reject-cert'}
        title={t('admin:trainer-verification.detail.cert_reject_title')}
        reasonLabel={t('admin:trainer-verification.detail.cert_reason_label')}
        reasonPlaceholder={t('admin:trainer-verification.detail.cert_reason_placeholder')}
        confirmLabel={t('admin:trainer-verification.detail.cert_reject')}
        destructive
        onClose={() => setReasonDialog(null)}
        onSubmit={handleReasonSubmit}
      />

      <TrainerReasonModal
        open={reasonDialog?.kind === 'revoke-cert'}
        title={t('admin:trainer-verification.detail.cert_revoke_title')}
        reasonLabel={t('admin:trainer-verification.detail.cert_reason_label')}
        reasonPlaceholder={t('admin:trainer-verification.detail.cert_reason_placeholder')}
        confirmLabel={t('admin:trainer-verification.detail.cert_revoke')}
        destructive
        onClose={() => setReasonDialog(null)}
        onSubmit={handleReasonSubmit}
      />

      <TrainerReasonModal
        open={reasonDialog?.kind === 'suspend'}
        title={t('admin:trainer-verification.detail.suspend_confirm_title')}
        description={detail.displayName}
        reasonLabel={t('admin:trainer-verification.detail.suspend_reason_label')}
        reasonPlaceholder={t('admin:trainer-verification.detail.suspend_reason_placeholder')}
        confirmLabel={t('admin:trainer-verification.detail.suspend')}
        destructive
        onClose={() => setReasonDialog(null)}
        onSubmit={handleReasonSubmit}
      />

      <TrainerReasonModal
        open={reasonDialog?.kind === 'force-deactivate'}
        title={t('admin:trainer-verification.detail.force_deactivate_confirm_title')}
        description={detail.displayName}
        reasonLabel={t('admin:trainer-verification.detail.force_deactivate_reason_label')}
        reasonPlaceholder={t(
          'admin:trainer-verification.detail.force_deactivate_reason_placeholder',
        )}
        confirmLabel={t('admin:trainer-verification.detail.force_deactivate')}
        destructive
        onClose={() => setReasonDialog(null)}
        onSubmit={handleReasonSubmit}
      />
    </div>
  );
}

export default TrainerVerificationDetailPage;
